import { useEffect, useState, useRef } from "react";
import { api, apiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ImagePlus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Fuar Standı", "Dekor & Tasarım", "Etkinlik Alanı", "Sahne & Prodüksiyon"];

export default function ReferencesPage() {
  const [refs, setRefs] = useState(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileInput = useRef(null);

  useEffect(() => {
    api.get("/references").then((r) => setRefs(r.data)).catch(() => setRefs([]));
  }, []);

  const add = async () => {
    if (title.trim().length < 2) return toast.error("Başlık girin");
    if (!file) return toast.error("Bir görsel seçin");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("files", file);
      const { data: uploaded } = await api.post("/upload", fd);
      const { data: ref } = await api.post("/references", { title, category, image_file_id: uploaded[0].id });
      setRefs((r) => [ref, ...r]);
      setTitle(""); setCategory(""); setFile(null);
      if (fileInput.current) fileInput.current.value = "";
      toast.success("Referans eklendi — landing sayfasında görünecek");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/references/${id}`);
      setRefs((r) => r.filter((x) => x.id !== id));
      toast.success("Referans silindi");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!refs) return <p className="text-slate-500 text-sm">Yükleniyor...</p>;

  return (
    <div className="space-y-8 fade-in max-w-4xl">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Web Sitesi</div>
        <h1 className="font-heading text-4xl font-extrabold tracking-tighter mt-1" data-testid="references-title">Referanslar</h1>
        <p className="text-sm text-slate-500 mt-2">Landing sayfasındaki "İşlerimiz" galerisinde gösterilecek tamamlanmış projeler.</p>
      </div>

      <div className="border border-slate-200 bg-white p-6 space-y-4">
        <h2 className="font-heading text-xl font-bold tracking-tight">Yeni Referans Ekle</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="ref-title">Başlık *</Label>
            <Input id="ref-title" data-testid="reference-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="örn. Teknoloji Zirvesi Ana Standı" className="rounded-none bg-[#F5F7FA] border-slate-200 h-11" />
          </div>
          <div className="space-y-1.5">
            <Label>Kategori</Label>
            <Select value={category} onValueChange={(v) => { if (v) setCategory(v); }}>
              <SelectTrigger className="rounded-none bg-[#F5F7FA] border-slate-200 h-11" data-testid="reference-category-select">
                <SelectValue placeholder="Seçin (opsiyonel)" />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-200 rounded-none">
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Fotoğraf *</Label>
          <input ref={fileInput} type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="reference-file-input" />
          <button type="button" onClick={() => fileInput.current?.click()} data-testid="reference-dropzone" className="w-full border border-dashed border-slate-300 bg-[#F5F7FA] hover:border-slate-400 transition-colors duration-200 p-6 flex flex-col items-center gap-2 text-slate-500">
            <ImagePlus size={18} />
            <span className="text-sm">{file ? file.name : "Proje fotoğrafı seçin (jpg, png)"}</span>
          </button>
        </div>
        <Button onClick={add} disabled={busy} data-testid="reference-add-button" className="rounded-none bg-slate-900 text-white hover:bg-slate-700 gap-2 font-semibold transition-colors duration-200">
          <ImagePlus size={15} /> {busy ? "Ekleniyor..." : "Referansı Ekle"}
        </Button>
      </div>

      {refs.length === 0 ? (
        <p className="text-slate-500 text-sm border border-slate-200 bg-white p-6" data-testid="no-references">Henüz referans eklenmedi. Eklediğiniz projeler landing sayfasında "İşlerimiz" bölümünde görünür.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {refs.map((r) => (
            <div key={r.id} className="border border-slate-200 bg-white group" data-testid={`reference-card-${r.id}`}>
              <img src={`${API}/public/reference-image/${r.image_file_id}`} alt={r.title} className="w-full aspect-[4/3] object-cover" />
              <div className="p-4 flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{r.title}</div>
                  {r.category && <div className="text-xs text-slate-500 mt-0.5">{r.category}</div>}
                </div>
                <button onClick={() => remove(r.id)} data-testid={`reference-delete-${r.id}`} className="text-slate-400 hover:text-red-600 transition-colors duration-200 shrink-0" title="Sil">
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
